import React from "react";
import { SimpleGrid, Text } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Page as SanityPage,
  type Customer,
  type IntegrationSimple,
  getPage,
  getSimpleIntegrations,
  getAnnouncementBanner,
  type Banner,
  getStackedLogos,
} from "lib/sanity";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { Feature } from "components/feature";
import { DemoEmbedHalf } from "components/demo-embed";
import { CenteredQuote } from "components/quotes";
import DestinationMarquee from "components/destination-marquee";
import { Link } from "ui/link";
import { getPageMetaImage } from "utils";
import { StackedLogos } from "components/logos";
import { PairImage } from "components/pair-image";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/data/hero.png";
import firstPairImage from "../../../public/assets/solutions/data/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/data/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/data/pair-3.svg";
import fourthPairImage from "../../../public/assets/solutions/data/pair-4.png";
import fifthPairImage from "../../../public/assets/solutions/data/pair-5.png";

type PageProps = {
  page: SanityPage;
  customerRows: Customer[][];
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
};

const Data: NextPage<Readonly<PageProps>> = ({ page, customerRows, destinations, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
      />

      <StackedLogos customerRows={customerRows} title="Trusted by teams at" />

      <Section center>
        <SimpleGrid columns={[1, 1, 2, 4]} width="100%" justifyItems="center" gap={12}>
          <Feature
            center
            title="Plug and play"
            image="/assets/solutions/data/feature-1.svg"
            description="Deep integrations into every part of your stack."
          />
          <Feature
            center
            title="Data consistency"
            image="/assets/solutions/data/feature-2.svg"
            description="Keep your warehouse as the source of truth."
          />
          <Feature
            center
            title="Beyond BI"
            image="/assets/solutions/data/feature-3.svg"
            description={
              <>
                Don’t stop at analytics.
                <br />
                Help teams take action.
              </>
            }
          />
          <Feature
            center
            title="Secure by design"
            image="/assets/solutions/data/feature-4.svg"
            description={
              <>
                Own your data.
                <br />
                Avoid lengthy security reviews.
              </>
            }
          />
        </SimpleGrid>
      </Section>

      <DemoEmbedHalf
        title="See an interactive demo"
        description={
          <Text>
            Walk though this 5 minute guided tour to learn how easy it is to sync data into any destination with Reverse
            ETL.
          </Text>
        }
      />

      <Section>
        <PairImage
          align="right"
          image={firstPairImage}
          actionText="Get started"
          actionUrl="/platform/reverse-etl"
          title="Say goodbye to custom API integrations"
          description={
            <Text>
              How much time and energy have you spent pouring into API documentation to build pipelines that get data
              from point A to point B? It’s time to reclaim that time. Reverse ETL lets you activate your data using
              SQL, or you can tap into your existing dbt models. We’ve built {integrationsCount}+ integrations into{" "}
              <Link href="/integrations" variant="article">
                SaaS tools
              </Link>
              ,{" "}
              <Link href="/blog/power-your-product-experiences-with-hightouch" variant="article">
                transactional databases
              </Link>
              , and{" "}
              <Link
                href="/blog/announcing-the-custom-destination-toolkit-build-your-own-destination-in-minutes"
                variant="article"
              >
                software frameworks
              </Link>{" "}
              so you can spend less time building data pipelines and more time building data systems that scale.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={secondPairImage}
          actionText="Learn more"
          actionUrl="/blog/looker-model-selector-announcement"
          title="Don’t let your data die in dashboards"
          description={
            <Text>
              They’re asking for a dashboard...but what they really want is data. Help business teams take action on
              customer data by syncing it directly into the tools they use every day. Just query your warehouse with
              SQL, or you can activate your existing Looker Looks. With the detailed data they need directly in their
              apps, they’ll probably be requesting fewer CSV exports, too. Win/win.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={thirdPairImage}
          actionText="Learn more"
          actionUrl="/platform/extensions"
          title="Tailor-built for the modern data stack"
          description={
            <Text>
              Building models should be the hardest part of your job. Full stop. Take advantage of them, and other
              engineering best practices, without the headaches. We’ve got all the bells-and-whistles you’ve come to
              expect: version control with{" "}
              <Link href="/blog/announcing-git-sync-back-your-syncs-with-version-control" variant="article">
                Git Sync
              </Link>
              , platform governance, orchestration, and observability.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={fourthPairImage}
          actionText="Learn more"
          actionUrl="/platform/security"
          title="Keep your data in your hands"
          description={
            <Text>
              Hightouch never stores your data...and we don’t want to. Run queries directly on your warehouse, and we’ll
              sync it directly to your destination. You maintain full control, with the additional reassurance that your
              data remains secure with SOC 2, HIPAA, and GDPR compliance.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={fifthPairImage}
          actionText="Learn more"
          actionUrl="/platform/reverse-etl"
          title={"Get your time\n(and sanity) back"}
          description={
            <Text>
              Data teams are woefully understaffed, and the imbalance between data consumers and data professionals is
              only getting worse. Create leverage for your teams by automating your data pipelines, taking advantage of
              data stack you’ve already built, and empowering business teams to self-serve with our no-code audience
              builder.
            </Text>
          }
        />
      </Section>

      <DestinationMarquee destinations={destinations} />

      <Section my={0} pb={sectionSpacingY}>
        <CenteredQuote
          maxW="4xl"
          quoteText="Our Salesforce data pipeline was the bane of our existence. It broke weekly, took 20 hours to complete, and adding a new field took hours because we would have to test it and figure out why it failed. With Hightouch, we can do all of this in seconds."
          author={{ name: "Seamus McKinsey", job: "Sr. Analytics Engineer" }}
          company={{
            name: "CircleCI",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/c44f1bc6f296efb2db1c241a6c91272475f91fce-360x75.svg",
          }}
          cta={{ href: "/customers/circleci", text: "Read customer story" }}
        />
      </Section>
    </Page>
  );
};

export default Data;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows] = await Promise.all([
    getPage("/solutions/data"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
